import {
  getVehicleImportErrorFile,
  getVehicleImportSummary,
  saveVehiclesImport,
} from '@api/vehicles';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useVehicleMassImportStore } from '@pages/vehicles/store';
import { ResponseErrorDto } from '@interfaces/models';
import { useTranslation } from 'react-i18next';
import { downloadFile } from '@helpers/utils';
import { toast } from '@ui/components';
import { useCallback } from 'react';
import { DateTime } from 'luxon';

export const useVehicleMassImport = (onImportSaved: () => void) => {
  const errorFileName = `SkyCash_import_error_log_${DateTime.now().toFormat('ddLLyyyy')}.csv`;
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const {
    setIsDownloadErrorFileLoading,
    isDownloadErrorFileLoading,
    setIsSaveImportLoading,
    setIsUploadFilePending,
    isSaveImportLoading,
    isUploadFilePending,
    setFileUploadError,
    fileUploadError,
    removeImport,
    showSummary,
    setSummary,
    dialogType,
    resetFile,
    summary,
    setFile,
    file,
  } = useVehicleMassImportStore();

  const { mutate: uploadFileMutation } = useMutation({
    mutationFn: (file: File) => getVehicleImportSummary(file),
    onMutate: () => {
      setSummary(null);
      setIsUploadFilePending(true);
    },
    onSuccess: (data) => {
      setSummary(data);
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      setFileUploadError(t(`API_ERRORS.${error.errorCode}`));
      setFile(null);
    },
    onSettled: () => {
      setIsUploadFilePending(false);
    },
  });

  const { mutate: downloadErrorFileCsv } = useMutation({
    mutationFn: getVehicleImportErrorFile,
    onMutate: () => {
      setIsDownloadErrorFileLoading(true);
    },
    onSuccess: (blob) => {
      downloadFile(blob, errorFileName);
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
    onSettled: () => {
      setIsDownloadErrorFileLoading(false);
    },
  });

  const { mutate: saveImport } = useMutation({
    mutationFn: saveVehiclesImport,
    onMutate: () => {
      setIsSaveImportLoading(true);
    },
    onSuccess: () => {
      onImportSaved();
      queryClient.invalidateQueries({ queryKey: ['vehicles-table'] });
      toast.success(t('VEHICLES.MASS_IMPORT.SUCCESS'));
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
    onSettled: () => {
      setIsSaveImportLoading(false);
    },
  });

  const uploadFile = useCallback(
    (value: File | null) => {
      setFile(value);

      if (!!value) {
        uploadFileMutation(value);
      }
    },
    [uploadFileMutation, setFile],
  );

  return {
    isDownloadErrorFileLoading,
    downloadErrorFileCsv,
    isSaveImportLoading,
    isUploadFilePending,
    fileUploadError,
    removeImport,
    showSummary,
    dialogType,
    saveImport,
    uploadFile,
    resetFile,
    summary,
    file,
  };
};
